import { Platform } from 'react-native'

/**
 * Opens a file from a URL in a new browser tab.
 * Fetches with auth headers, creates a blob URL, and opens it.
 */
export const openFileInBrowser = async (
  url: string,
  headers?: Record<string, string>
) => {
  if (Platform.OS !== 'web') {
    throw new Error('openFileInBrowser is only available on web')
  }

  const response = await fetch(url, {
    headers: headers || {},
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.statusText}`)
  }

  const blob = await response.blob()
  const blobUrl = URL.createObjectURL(blob)
  window.open(blobUrl, '_blank')

  // Revoke after a delay to give the browser time to load the content
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
}

/**
 * Downloads a file from a URL with a specific filename on web
 */
export const downloadFileFromUrl = async (
  url: string,
  filename: string,
  headers?: Record<string, string>
) => {
  if (Platform.OS !== 'web') {
    throw new Error('downloadFileFromUrl is only available on web')
  }

  try {
    const response = await fetch(url, {
      headers: headers || {},
    })

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`)
    }

    const blob = await response.blob()
    downloadBlob(blob, filename)
  } catch (error) {
    console.error('Error downloading file:', error)
    throw error
  }
}

/**
 * Downloads a blob with a specific filename
 */
export const downloadBlob = (blob: Blob, filename: string) => {
  if (Platform.OS !== 'web') {
    throw new Error('downloadBlob is only available on web')
  }

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.style.display = 'none'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  // Clean up the URL object
  URL.revokeObjectURL(url)
}

/**
 * Downloads a base64 string as a file with a specific filename
 */
export const downloadBase64 = (
  base64: string,
  filename: string,
  mimeType: string
) => {
  if (Platform.OS !== 'web') {
    throw new Error('downloadBase64 is only available on web')
  }

  const base64ToBlob = (base64: string, mimeType: string) => {
    const byteCharacters = atob(base64)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    return new Blob([byteArray], { type: mimeType })
  }

  const blob = base64ToBlob(base64, mimeType)
  downloadBlob(blob, filename)
}

/**
 * Generates a nice filename for a document based on its type and context
 */
export const generateDocumentFilename = (
  fileName: string | null | undefined,
  mimeType: string | null | undefined,
  documentType?: string,
  fosterName?: string,
  contextName?: string
): string => {
  // If we have a fileName, use it as is
  if (fileName) {
    return fileName
  }

  // Get file extension from mime type
  const getExtension = (mimeType: string) => {
    const mimeToExt: Record<string, string> = {
      'application/pdf': 'pdf',
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/webp': 'webp',
      'image/heic': 'heic',
      'text/plain': 'txt',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        'docx',
    }
    return mimeToExt[mimeType] || 'file'
  }

  const extension = mimeType ? getExtension(mimeType) : 'file'

  // Build filename based on available context
  const parts: string[] = []

  if (fosterName) {
    parts.push(fosterName.replace(/[^a-zA-Z0-9]/g, '_'))
  }

  if (contextName) {
    parts.push(contextName.replace(/[^a-zA-Z0-9]/g, '_'))
  } else if (documentType) {
    // Convert document type to readable format
    const typeMap: Record<string, string> = {
      AdoptionContract: 'adoption_contract',
      MedicalRecord: 'medical_record',
      IntakeDocument: 'intake_document',
      OwnerSurrenderForm: 'owner_surrender_form',
      TrialRunAgreement: 'trial_run_agreement',
      Uncategorized: 'document',
    }
    parts.push(typeMap[documentType] || 'document')
  }

  // Add timestamp if we don't have much context
  if (parts.length === 0) {
    parts.push('document')
  }

  const baseName = parts.join('_')
  return `${baseName}.${extension}`
}
